import { AppError } from './errors.js';
import { err, ok, type Result } from './result.js';

export interface Identifiable {
  readonly id: string;
}

/**
 * Registro generico de elementos identificables (conectores, proveedores de IA,
 * visualizaciones, dialectos...). Es la pieza base del sistema de plugins.
 *
 * @example
 * const registry = new Registry<Connector>('connector');
 * registry.register(postgresConnector);
 * const connector = registry.require('postgresql');
 */
export class Registry<T extends Identifiable> {
  readonly #items = new Map<string, T>();
  readonly #kind: string;

  /** @param kind Nombre del tipo de elemento, usado en los mensajes de error. */
  constructor(kind: string) {
    this.#kind = kind;
  }

  register(item: T): Result<T, AppError> {
    if (this.#items.has(item.id)) {
      return err(
        new AppError('CONFLICT', `Ya existe un ${this.#kind} registrado con id "${item.id}".`),
      );
    }
    this.#items.set(item.id, item);
    return ok(item);
  }

  unregister(id: string): boolean {
    return this.#items.delete(id);
  }

  has(id: string): boolean {
    return this.#items.has(id);
  }

  get(id: string): T | undefined {
    return this.#items.get(id);
  }

  require(id: string): Result<T, AppError> {
    const item = this.#items.get(id);
    return item === undefined
      ? err(new AppError('NOT_FOUND', `No existe un ${this.#kind} registrado con id "${id}".`))
      : ok(item);
  }

  list(): readonly T[] {
    return [...this.#items.values()];
  }

  get size(): number {
    return this.#items.size;
  }
}
